/**
 * useTodayTasks — Work hub "Da fare oggi" + "Completate · oggi" feed.
 *
 * BE source of truth: ``GET /api/v1/tasks/today`` in
 * ``ando-agent/src/api/v1/endpoints/tasks.py``. Single roundtrip pulls
 * both pending + completed_today sections plus the hero counts (overdue,
 * estimated total minutes), so the surface stays consistent across renders.
 *
 * Cache shape: one TanStack Query keyed ``["tasks", "today"]``. The three
 * mutations (complete / uncomplete / create) invalidate that key so the
 * hub re-fetches a fresh, consistent feed.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type CreateTaskInput,
  type PatchTaskInput,
  type TaskItem,
  type TasksTodayResponse,
  completeTask,
  createTask,
  listTodayTasks,
  patchTask,
  uncompleteTask,
} from "@ando/core";

/** Stable query key — exported for cross-feature invalidation hooks. */
export const todayTasksQueryKey = ["tasks", "today"] as const;

/**
 * Shared query definition for the Work hub task feed. Consumed by BOTH the
 * ``useTodayTasks`` hook and the ``/workspace/work`` route loader
 * (``ensureQueryData``) so the intent-preload warms the SAME cache entry the
 * hub reads — the tab paints from cache instead of spinning. Query CONFIG
 * only — the hook layers ``refetchInterval`` on top.
 */
export function todayTasksQueryOptions() {
  return queryOptions<TasksTodayResponse>({
    queryKey: todayTasksQueryKey,
    queryFn: () => listTodayTasks(),
    // 30s — tasks don't change as quickly as inbox proposals (15s) but should
    // still pick up edits made elsewhere (e.g. Ando creating a task via chat).
    staleTime: 30_000,
  });
}

/** Fetch + cache the Work hub task feed. */
export function useTodayTasks() {
  return useQuery({
    ...todayTasksQueryOptions(),
    refetchInterval: 30_000,
  });
}

/** Mark a task as completed. Invalidates the feed query on success. */
export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (taskId) => completeTask(taskId),
    onSuccess: () => qc.invalidateQueries({ queryKey: todayTasksQueryKey }),
  });
}

/** Restore a completed task back to pending. */
export function useUncompleteTask() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (taskId) => uncompleteTask(taskId),
    onSuccess: () => qc.invalidateQueries({ queryKey: todayTasksQueryKey }),
  });
}

/** Create a brand-new task — used by the "+ Nuovo task" entry point. */
export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, CreateTaskInput>({
    mutationFn: (input) => createTask(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: todayTasksQueryKey }),
  });
}

/**
 * Patch a task — snooze (``due_at``), repriority, edit, or dismiss
 * (``is_deleted: true``). Mirrors the BE ``PATCH /tasks/{task_id}``.
 *
 * Dismissals optimistically remove the row from the cache before the
 * BE roundtrip so the user sees the UI react instantly; on error we
 * roll back via re-fetch (TanStack ``onError`` invalidates).
 */
export function usePatchTask() {
  const qc = useQueryClient();
  return useMutation<
    TaskItem,
    Error,
    { taskId: string; patch: PatchTaskInput }
  >({
    mutationFn: ({ taskId, patch }) => patchTask(taskId, patch),
    onMutate: async ({ taskId, patch }) => {
      // Only optimistically remove on dismissal — snooze / repriority
      // wait for the server roundtrip so the FE never lies about the
      // actual due_at (timezone math is server-authoritative).
      if (patch.is_deleted !== true) return;
      await qc.cancelQueries({ queryKey: todayTasksQueryKey });
      const previous = qc.getQueryData<TasksTodayResponse>(todayTasksQueryKey);
      if (!previous) return { previous };
      const drop = (rows: readonly TaskItem[]) =>
        rows.filter((r) => r.task_id !== taskId);
      qc.setQueryData<TasksTodayResponse>(todayTasksQueryKey, {
        ...previous,
        pending: drop(previous.pending),
        upcoming: drop(previous.upcoming),
        completed_today: drop(previous.completed_today),
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back the optimistic drop.
      const previous = (ctx as { previous?: TasksTodayResponse } | undefined)?.previous;
      if (previous) {
        qc.setQueryData(todayTasksQueryKey, previous);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: todayTasksQueryKey }),
  });
}
